import React, { useEffect, useState } from 'react';

const IgnitionThrustContext = ({
  loadingScore,
  ignitionContext,
  ignitionThrust,
  handleRetry
}) => {
  const totalBars = 24;
  const [activeBars, setActiveBars] = useState(Array(totalBars).fill(false));
  const [currentBar, setCurrentBar] = useState(0);
  const [scoreDisplay, setScoreDisplay] = useState('0%');
  const [scoreClass, setScoreClass] = useState('score');
  const [tooltipLow, setTooltipLow] = useState({});
  const [tooltipMid, setTooltipMid] = useState({});
  const [tooltipHigh, setTooltipHigh] = useState({});

  // Simulate the loading animation
  useEffect(() => {
    let interval;

    if (loadingScore) {
      // Start loading animation
      interval = setInterval(() => {
        setCurrentBar((prev) => (prev + 1) % totalBars);
      }, 50);
    } else {
      // Stop loading animation and reset bars
      setActiveBars(Array(totalBars).fill(false));
      setCurrentBar(0);
    }

    return () => clearInterval(interval);
  }, [loadingScore]);

  // Update active bars based on currentBar during loading
  useEffect(() => {
    if (loadingScore) {
      const updatedActiveBars = Array(totalBars)
        .fill(false)
        .map((_, index) => index <= currentBar);
      setActiveBars(updatedActiveBars);
    }
  }, [currentBar, loadingScore]);

  // Update display when data is loaded
  useEffect(() => {
    if (!loadingScore && ignitionThrust != null && ignitionContext != null) {
      // Ensure ignitionContext is not null or undefined

      // Fix typo in 'Scale Ceiling'
      const scaleCeilingValue =
        ignitionContext['Scale Ceiling'] || ignitionContext['Scale Celing'];
      const scaleCeiling = parseFloat(scaleCeilingValue) || 100; // Default to 100 if undefined or invalid

      const scoreNumber = ignitionThrust * 100;
      const scorePercentage = (scoreNumber * 100) / scaleCeiling;
      const percentageIndex = Math.ceil((scorePercentage * totalBars) / 100);

      const updatedActiveBars = Array(totalBars)
        .fill(false)
        .map((_, index) => index < percentageIndex);
      setActiveBars(updatedActiveBars);

      let lowExampleScore = '';
      let midExampleScore = '';
      let medianExampleScore = '';

      if (scoreNumber > 3000) {
        setScoreDisplay('30X+');
        medianExampleScore = '30X+';
      } else if (scoreNumber > 500) {
        setScoreDisplay((scoreNumber / 100).toFixed(1) + 'X');
        lowExampleScore =
          (
            (parseFloat(ignitionContext['Low Example Score']) || 0) / 100
          ).toFixed(1) + 'X';
        midExampleScore =
          (
            (parseFloat(ignitionContext['Mid - Low Example Score']) || 0) / 100
          ).toFixed(1) + 'X';
        medianExampleScore = (scaleCeiling / 100).toFixed(1) + 'X';
      } else {
        setScoreDisplay(Math.round(scoreNumber) + '%');
        lowExampleScore = ignitionContext['Low Example Score'];
        midExampleScore = ignitionContext['Mid - Low Example Score'];
        medianExampleScore = scaleCeilingValue;
      }

      if (percentageIndex < 6) {
        setScoreClass('score low');
      } else if (percentageIndex < 13) {
        setScoreClass('score mid-low');
      } else if (percentageIndex < 20) {
        setScoreClass('score mid-high');
      } else {
        setScoreClass('score high');
      }

      // Update tooltips
      setTooltipLow({
        name: ignitionContext['Low Example Name'],
        date: ignitionContext['Low Date'],
        score: lowExampleScore,
        mentions: ignitionContext['Low Mentions'],
        reach: ignitionContext['Low Reach']
      });

      setTooltipMid({
        name: ignitionContext['Mid - Low Example Name'],
        date: ignitionContext['Mid Date'],
        score: midExampleScore,
        mentions: ignitionContext['Mid Mentions'],
        reach: ignitionContext['Mid Reach']
      });

      setTooltipHigh({
        name: ignitionContext['Median Example'],
        date: ignitionContext['High Date'],
        score: medianExampleScore,
        mentions: ignitionContext['High Mentions'],
        reach: ignitionContext['High Reach']
      });
    }
  }, [loadingScore, ignitionThrust, ignitionContext]);

  // Positioning tooltips
  let scoreCeiling = 100;
  let scoreLow = 0;
  let scoreMid = 0;
  let scoreLowPosition = 0;
  let scoreMidPosition = 0;
  if (ignitionContext) {
    // Fix typo in 'Scale Ceiling'
    const scaleCeilingValue =
      ignitionContext['Scale Ceiling'] || ignitionContext['Scale Celing'];
    scoreCeiling = parseFloat(scaleCeilingValue) || 100;

    scoreLow = parseFloat(ignitionContext['Low Example Score']) || 0;
    scoreMid = parseFloat(ignitionContext['Mid - Low Example Score']) || 0;
    scoreLowPosition = (scoreLow * 100) / scoreCeiling;
    scoreMidPosition = (scoreMid * 100) / scoreCeiling;
  }

  return (
    <div className="fuselight-ignition-thrust-context">
      <h2>Ignition Thrust</h2>
      {loadingScore ? (
        // Show loading animation
        <div className="bar-scale-container">
          <div className="bar-scale">
            {activeBars.map((isActive, index) => (
              <div key={index} className={isActive ? 'bar-active' : ''}></div>
            ))}
          </div>
        </div>
      ) : ignitionThrust != null && ignitionContext != null ? (
        // Show data
        <>
          <h2 className={scoreClass}>{scoreDisplay}</h2>
          <div className="bar-scale-container">
            <div className="bar-scale">
              {activeBars.map((isActive, index) => (
                <div key={index} className={isActive ? 'bar-active' : ''}></div>
              ))}
            </div>
            <div className="bar-scale-labels">
              <div className="label-low">{ignitionContext['Scale Floor']}</div>
              <div className="label-high">{tooltipHigh.score}</div>
            </div>
            {scoreLowPosition !== undefined && !isNaN(scoreLowPosition) && (
              <div className="tooltip-low">
                <div
                  className="tooltip-hover"
                  style={{ left: scoreLowPosition + '%' }}
                >
                  <i className="fas fa-info"></i>
                </div>
                <div className="tooltip-text">
                  <p>
                    <span>{tooltipLow.name}</span>
                    <br />
                    in {tooltipLow.date}
                    <br />
                    <span>Ignition Thrust:</span>{' '}
                    <span className="score">{tooltipLow.score}</span>
                    <br />
                    Mentions: {tooltipLow.mentions}
                    {/* <br />
                    Reach: {tooltipLow.reach} */}
                  </p>
                </div>
              </div>
            )}
            {scoreMidPosition !== undefined && !isNaN(scoreMidPosition) && (
              <div className="tooltip-mid">
                <div
                  className="tooltip-hover"
                  style={{ left: scoreMidPosition + '%' }}
                >
                  <i className="fas fa-info"></i>
                </div>
                <div className="tooltip-text">
                  <p>
                    <span>{tooltipMid.name}</span>
                    <br />
                    in {tooltipMid.date}
                    <br />
                    <span>Ignition Thrust:</span>{' '}
                    <span className="score">{tooltipMid.score}</span>
                    <br />
                    Mentions: {tooltipMid.mentions}
                    {/* <br />
                    Reach: {tooltipMid.reach} */}
                  </p>
                </div>
              </div>
            )}
            {/* Tooltip for High Score */}
            <div className="tooltip-high">
              <div className="tooltip-hover" style={{ left: '100%' }}>
                <i className="fas fa-info"></i>
              </div>
              <div className="tooltip-text">
                <p>
                  <span>{tooltipHigh.name}</span>
                  <br />
                  in {tooltipHigh.date}
                  <br />
                  <span>Ignition Thrust:</span>{' '}
                  <span className="score">{tooltipHigh.score}</span>
                  <br />
                  Mentions: {tooltipHigh.mentions}
                  {/* <br />
                  Reach: {tooltipHigh.reach} */}
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="retry-flex">
          <p className="retry-header">Something went wrong...</p>
          <button className="btn btn--primary-outline" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default IgnitionThrustContext;
